/**
 * User Avatar Component
 *
 * Displays the current user's avatar and provides logout functionality.
 */
import React, { useState, useRef, useEffect } from 'react';
import { FiUser, FiLogOut, FiChevronDown, FiShield } from 'react-icons/fi';
import { useAuth } from '../../auth/AuthContext';

interface UserAvatarProps {
  size?: number;
  showDropdown?: boolean;
}

const UserAvatar: React.FC<UserAvatarProps> = ({ size = 36, showDropdown = true }) => {
  const { user, isAuthenticated, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isAuthenticated || !user) {
    return null;
  }

  const handleLogout = () => {
    setIsOpen(false);
    logout();
  };

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map((part) => part[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  return (
    <div className="user-avatar-container position-relative" ref={dropdownRef}>
      <button
        className="btn btn-link p-0 d-flex align-items-center gap-2"
        onClick={() => showDropdown && setIsOpen(!isOpen)}
        style={{ textDecoration: 'none' }}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* Avatar */}
        <div
          className="user-avatar rounded-circle overflow-hidden flex-shrink-0"
          style={{
            width: size,
            height: size,
            background: user.photoUrl ? 'transparent' : '#0077E6',
            border: '2px solid #e2e8f0',
          }}
        >
          {user.photoUrl ? (
            <img
              src={user.photoUrl}
              alt={user.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <div
              className="d-flex align-items-center justify-content-center h-100"
              style={{ color: 'white', fontSize: size * 0.4, fontWeight: 600 }}
            >
              {getInitials(user.name)}
            </div>
          )}
        </div>

        {/* Chevron */}
        {showDropdown && (
          <FiChevronDown
            size={16}
            className="text-muted"
            style={{
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          />
        )}
      </button>

      {/* Dropdown Menu */}
      {showDropdown && isOpen && (
        <div
          className="user-dropdown position-absolute end-0 mt-2 py-2 bg-white rounded shadow-lg"
          style={{
            minWidth: '240px',
            zIndex: 1050,
            border: '1px solid #e2e8f0',
          }}
        >
          {/* User Info */}
          <div className="px-4 py-3 border-bottom">
            <div className="d-flex align-items-center gap-3">
              <div
                className="rounded-circle overflow-hidden flex-shrink-0"
                style={{
                  width: 48,
                  height: 48,
                  background: user.photoUrl ? 'transparent' : '#0077E6',
                }}
              >
                {user.photoUrl ? (
                  <img
                    src={user.photoUrl}
                    alt={user.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <div
                    className="d-flex align-items-center justify-content-center h-100"
                    style={{ color: 'white' }}
                  >
                    <FiUser size={24} />
                  </div>
                )}
              </div>
              <div className="flex-grow-1 overflow-hidden">
                <div className="fw-semibold text-truncate">{user.name}</div>
                <div className="text-muted small text-truncate">{user.email}</div>
              </div>
            </div>

            {/* Role Badge */}
            {user.isAuditor && (
              <div className="mt-2">
                <span
                  className="badge d-inline-flex align-items-center gap-1"
                  style={{
                    background: '#DBEAFE',
                    color: '#1D4ED8',
                    fontWeight: 500,
                  }}
                >
                  <FiShield size={12} />
                  Auditor Médico
                </span>
              </div>
            )}
          </div>

          {/* Logout */}
          <div className="px-2 pt-2">
            <button
              className="btn btn-link text-danger d-flex align-items-center gap-2 w-100 px-3 py-2"
              onClick={handleLogout}
              style={{
                textDecoration: 'none',
                borderRadius: '6px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#FEE2E2';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <FiLogOut size={16} />
              Cerrar sesión
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAvatar;
